<script lang="ts" setup>
import type { ProductModelV2, ProductSearchReqModel } from '@develop/ia-api-library'
import { searchProductsV2 } from '@develop/ia-api-library'
import { PageType } from '@/models/pages'

definePageMeta({
  type: PageType.WhatsAppCreateCartPage,
  layout: 'empty-centered',
})

const route = useRoute()
const { t } = useTranslation()

const cartStore = useCartStore()
const checkoutStore = useCheckoutStore()
const currentPharmacyStore = useCurrentPharmacyStore()

const { getMetaUrl } = useCdnUrl()

const pharmacyLogo = computed(() => currentPharmacyStore.pharmacy?.additionalInformation.logo)

const imageForShow = pharmacyLogo.value || getMetaUrl(t('meta.default.image'))

useSeoMeta({
  ogImage: imageForShow,
  twitterImage: imageForShow,
})

const hasMissingParameters = ref(false)

const transformProducts = (input: Record<string, any>): { pzn: string; quantity: number }[] =>
  Object.entries(input)
    .filter(([key]) => /^products\[\d+\]\[pzn\]$/.test(key))
    .map(([key, pzn]) => {
      const index = key.match(/^products\[(\d+)\]\[pzn\]$/)![1]
      const quantityKey = `products[${index}][quantity]`
      const quantity = parseInt(input[quantityKey], 10) || 0
      return { pzn, quantity }
    })

const routePharmacyId = route.query.pharmacyId ? +route.query.pharmacyId : undefined
const routeProducts = transformProducts(route.query)

const currentPharmacyId = currentPharmacyStore.pharmacyId
const cartProducts = cartStore.cartData.products

const searchProducts = async (pzns: string[]) => {
  const payload: ProductSearchReqModel = {
    searchTerm: 'match_all',
    sorting: 'name-asc',
    pharmacyId: currentPharmacyStore.pharmacyId!,
    resultsPerPage: routeProducts.length,
    activeSubstances: [],
    categories: [],
    dosages: [],
    producers: [],
    products: pzns,
    type: 'pharmacy',
    volumes: [],
  }

  const response = await searchProductsV2(1, payload)
  return response.data?.results || []
}

const getProductsFromRoute = async () => {
  if (!routePharmacyId || !routeProducts.length) {
    hasMissingParameters.value = true
    return
  }

  if (routePharmacyId !== currentPharmacyId) {
    checkoutStore.reset()
    await currentPharmacyStore.ensure(routePharmacyId)
    await ensureCookieAndUserPharmacy(routePharmacyId)
  }

  const productsWithDetails = await searchProducts(routeProducts.map(product => product.pzn))
  const availableProducts = productsWithDetails.filter(product => product.availability?.status.value)
  const unavailableProducts = productsWithDetails.filter(product => !product.availability?.status.value)
  unavailableProducts.forEach(product => cartStore.addUnavailableProduct({
    pzn: product.pzn,
    name: product.name,
  }))

  return availableProducts
}

const addProductsToCart = async (products: ProductModelV2[]) => {
  if (!products?.length)
    return

  products.forEach(async (product) => {
    const alreadySelectedProduct = cartProducts.find(p => p.pzn === product.pzn)
    const routeProductQuantity = routeProducts.find(routeProduct => product.pzn === routeProduct.pzn)?.quantity ?? 0

    // Prevent adding more than max quantity
    const totalQuantity = Math.min(
      routeProductQuantity + (alreadySelectedProduct?.amount || 0),
      product.maxQuantity,
    )

    alreadySelectedProduct
      ? cartStore.updateProductAmount(product, totalQuantity)
      : cartStore.addProduct(product, totalQuantity)
  })

  await checkoutStore.updateDraft()
}

const navigateToCart = () => {
  const draftId = checkoutStore.draftId

  if (!draftId) {
    customNavigateTo({
      name: 'warenkorb-einzelheiten',
    })
    return
  }

  customNavigateTo({
    name: 'warenkorb-einzelheiten-draftId',
    params: { draftId: checkoutStore.draftId },
  })
}

onMounted(async () => {
  const routeProductsWithDetails = await getProductsFromRoute()
  await addProductsToCart(routeProductsWithDetails || [])

  navigateToCart()
})
</script>

<template>
  <div class="flex flex-col flex-center max-w-140 text-center">
    <IaAlert
      v-if="hasMissingParameters"
      type="error"
    >
      {{ t('whatsApp.cartProcess.missingParameters') }}
    </IaAlert>
    <div v-else>
      <!-- Loading spinner doesn't need conditional rendering because this page automatically redirects to cart page after script is executed  -->
      <IaLoadingSpinner />
      <IaHeading
        :level="5"
        as="h1"
        class="mt-8 font-700"
      >
        {{ t('whatsApp.cartProcess.title') }}
      </IaHeading>
      <IaParagraph class="mt-4 !text-grey-600">
        {{ t('whatsApp.cartProcess.subtitle') }}
      </IaParagraph>
    </div>
  </div>
</template>
